// The Security Operations incident queue (/soc): one row per open, assignable risk.
//
// Rows come from one read, GET /unifiedRisks/details (QUEUE_DETAILS_URL): every risk of the queue's
// types the user can see, with assignee, pending invite, remediation and tickets. Its riskType is the
// frontend bucket ("External" for every type below), so the type is read from:
//  - riskSource for Defender XDR: only incidents and alerts are queued (the read asks for no other
//    Defender source), the same two sources the summary counts as "Open incidents & alerts";
//  - controlSource for the others, which each of these risk types' single generator sets to a distinct
//    value. AI posture risks (AiAsset, controlSource AiDiscovery) are queued too, under Agentic AI: the
//    same open set the Agentic AI tile counts as open AI posture risks. Of these, rows whose riskType
//    is not "External" are skipped (legacy GRC copies of the same findings).
// "Open" and Defender severity follow the summary: status identified or in_progress, inherent rating.

import { parseUtc } from "./socFreshness";

export const DEFENDER_QUEUE_SOURCES = {
  DefenderXdrIncident: "Incident",
  DefenderXdrAlert: "Alert",
};

// The details read for the queue: only the risk types it can show, only open ones, and of Defender XDR
// only incidents and alerts. The full list (every type, every status, every CVE and recommendation) is
// thousands of rows the queue would discard.
export const QUEUE_RISK_TYPES = ["DefenderXDR", "ESM", "SecretsScan", "DarkWebExposure", "RansomwareLiveThreat", "AiAsset"];
export const QUEUE_DETAILS_URL = `/unifiedRisks/details?riskTypes=${QUEUE_RISK_TYPES.join(",")}&openOnly=true`
  + `&defenderSources=${Object.keys(DEFENDER_QUEUE_SOURCES).join(",")}`;

// controlSource -> queue source, for the risk types other than Defender XDR.
export const EXTERNAL_SOURCES = {
  SurfaceScan: { source: "Attack surface", area: "external" },
  SecretsScan: { source: "Exposed secret", area: "external" },
  DarkWebLiveMonitoring: { source: "Leaked credential", area: "external" },
  // Ransomware leak-site listings of the org's own domains (risk type RansomwareLiveThreat).
  RansomwareLiveMonitoring: { source: "Ransomware", area: "external" },
  AiDiscovery: { source: "AI posture", area: "agenticAi" },
};

export const QUEUE_AREAS = {
  incidents: "Incidents & alerts",
  external: "External threat",
  agenticAi: "Agentic AI",
};

export const SEVERITIES = ["critical", "high", "medium", "low"];
const SEVERITY_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low", unrated: "Unrated" };
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3, unrated: 4 };

const lower = (v) => String(v ?? "").trim().toLowerCase();
export const isOpenStatus = (status) => ["identified", "in_progress"].includes(lower(status));
export const severityKey = (rating) => (SEVERITIES.includes(lower(rating)) ? lower(rating) : "unrated");
export const severityLabel = (key) => SEVERITY_LABEL[key] || SEVERITY_LABEL.unrated;

const toRow = ({ id, title, severity, status, source, area, detail, now }) => {
  const created = parseUtc(detail?.createdAt);
  return {
    id,
    title: title || "Untitled risk",
    severity,
    status: lower(status) === "in_progress" ? "In progress" : "New",
    source,
    area,
    areaLabel: QUEUE_AREAS[area],
    assignedToUserId: detail?.assignedToUserId ?? null,
    assignedToUserName: detail?.assignedToUserName || null,
    // Assigned to an invited user who hasn't joined yet.
    invitePending: detail?.assignedToUserInviteId != null,
    assignedAt: parseUtc(detail?.assignedAt),
    created,
    ageMs: created ? Math.max(0, now.getTime() - created.getTime()) : null,
    // For the assign dialogs' activity log.
    riskType: detail?.riskType || null,
    description: detail?.riskDescription || null,
    remediationId: detail?.remediationId ?? null,
    remediationTitle: detail?.remediationTitle || null,
    actionDescription: detail?.actionDescription || null,
    jiraIssueKey: detail?.jiraIssueKey || null,
    jiraIssueUrl: detail?.jiraIssueUrl || null,
    serviceNowIncidentNumber: detail?.serviceNowIncidentNumber || null,
    serviceNowIncidentUrl: detail?.serviceNowIncidentUrl || null,
    progressPercentage: detail?.progressPercentage ?? null,
    remediationTargetDate: detail?.remediationTargetDate ?? null,
  };
};

// The queue row for one RiskDetailsDto, or null when the queue doesn't show it.
const queueRowOf = (d, now) => {
  if (!d?.id || !isOpenStatus(d.status)) return null;
  const kind = DEFENDER_QUEUE_SOURCES[d.riskSource];
  if (kind) {
    return toRow({
      id: d.id,
      title: d.riskTitle,
      severity: severityKey(d.inherentRiskRating),
      status: d.status,
      source: `Defender XDR · ${kind}`,
      area: "incidents",
      detail: d,
      now,
    });
  }
  // A Defender XDR row of another source (a CVE, a recommendation) is not queued.
  if (lower(d.riskSource).startsWith("defenderxdr")) return null;
  // Only the external risk types, as the summary counts them. Risks copied from the legacy GRC table
  // (riskType "GRC") can carry an external control source and duplicate the live risk for a finding.
  const ext = EXTERNAL_SOURCES[d.controlSource];
  if (!ext || d.riskType !== "External") return null;
  return toRow({
    id: d.id,
    title: d.riskTitle,
    severity: severityKey(d.riskSeverity),
    status: d.status,
    source: ext.source,
    area: ext.area,
    detail: d,
    now,
  });
};

// details: GET /unifiedRisks/details (array).
export const buildQueue = (details, now = new Date()) => {
  const rows = new Map();
  (details || []).forEach((d) => {
    if (rows.has(d?.id)) return;
    const row = queueRowOf(d, now);
    if (row) rows.set(row.id, row);
  });
  return sortQueue([...rows.values()]);
};

// Most severe first, then oldest first; unknown age last within its severity.
export const sortQueue = (rows) =>
  [...rows].sort((a, b) =>
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    || (b.ageMs ?? -1) - (a.ageMs ?? -1)
    || String(a.title).localeCompare(String(b.title)));

export const isUnassigned = (row) => row.assignedToUserId == null && !row.invitePending;
export const isMine = (row, userId) => userId != null && row.assignedToUserId != null
  && String(row.assignedToUserId) === String(userId);

// tab: "all" | "unassigned" | "mine"; severity: "all" or a key; area: null or an area key.
export const filterQueue = (rows, { tab = "all", severity = "all", area = null, source = null, userId = null } = {}) =>
  rows.filter((r) =>
    (!area || r.area === area)
    && (!source || r.source === source)
    && (tab === "all" || (tab === "unassigned" ? isUnassigned(r) : isMine(r, userId)))
    && (severity === "all" || r.severity === severity));

export const countBy = (rows, key) =>
  rows.reduce((acc, r) => ({ ...acc, [r[key]]: (acc[r[key]] || 0) + 1 }), {});

// Unassigned critical and high rows: the "needs an owner now" KPI.
export const unassignedUrgent = (rows) =>
  rows.filter((r) => isUnassigned(r) && (r.severity === "critical" || r.severity === "high")).length;

